import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "./ui/dialog";
import { ClockCounterClockwise, ArrowUUpLeft, GitDiff, Books } from "@phosphor-icons/react";

function lineDiff(oldText = "", newText = "") {
  const o = new Set(oldText.split("\n").map((l) => l.trim()).filter(Boolean));
  const n = new Set(newText.split("\n").map((l) => l.trim()).filter(Boolean));
  const removed = [...o].filter((l) => !n.has(l));
  const added = [...n].filter((l) => !o.has(l));
  return { added, removed };
}

export default function VersionHistory({ contentId, primaryLang = "es", onChanged }) {
  const [versions, setVersions] = useState([]);
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/content/${contentId}/versions`);
    setVersions(data);
  };
  useEffect(() => { load(); }, [contentId]);

  const toggle = (id) => setSel((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id].slice(-2));

  const restore = async (v) => {
    setBusy(true);
    try {
      await api.post(`/content/${contentId}/rollback/${v.id}`);
      toast.success(`Restauration de la version ${v.version} lancée`, { description: "Un brouillon à valider a été créé." });
      onChanged && onChanged();
    } catch (e) { toast.error("Restauration impossible"); } finally { setBusy(false); }
  };

  const [va, vb] = sel.map((id) => versions.find((v) => v.id === id)).filter(Boolean);
  const diff = va && vb ? lineDiff(
    (va.version < vb.version ? va : vb).translations?.[primaryLang]?.body_markdown,
    (va.version < vb.version ? vb : va).translations?.[primaryLang]?.body_markdown) : null;

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button data-testid="version-history-btn" className="w-full flex items-center justify-center gap-2 border border-border hover:bg-secondary py-2 rounded-md text-sm transition-colors">
          <ClockCounterClockwise size={16} /> Historique des versions
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-brand">Historique des versions</DialogTitle>
          <DialogDescription>Sélectionnez deux versions pour comparer, ou restaurez une version antérieure (créée en brouillon à valider).</DialogDescription>
        </DialogHeader>

        <div className="space-y-2 mt-2">
          {versions.length === 0 && <p className="text-sm text-muted-foreground">Aucune version enregistrée pour l'instant (les versions sont créées à chaque publication).</p>}
          {versions.map((v) => (
            <div key={v.id} data-testid={`version-${v.version}`}
              className={`border rounded-md p-3 transition-colors ${sel.includes(v.id) ? "border-ai ring-1 ring-ai/20" : "border-border"}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={sel.includes(v.id)} onChange={() => toggle(v.id)} data-testid={`version-select-${v.version}`} />
                  <span className="font-mono text-xs bg-brand-soft text-brand px-2 py-0.5 rounded">v{v.version}</span>
                  <span className="text-sm font-medium">{v.reason}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[11px] text-muted-foreground">{new Date(v.created_at).toLocaleString("fr-FR")}</span>
                  <span className="text-[11px] font-mono text-brand">SEO {v.seo_score}</span>
                  <span className="text-[11px] font-mono text-ai">GEO {v.geo_score}</span>
                  <button data-testid={`restore-${v.version}`} onClick={() => restore(v)} disabled={busy}
                    className="flex items-center gap-1 text-xs text-brand hover:underline"><ArrowUUpLeft size={13} /> Restaurer</button>
                </div>
              </div>
              {v.sources?.length > 0 && (
                <div className="mt-2 flex items-start gap-1.5 text-[11px] text-muted-foreground">
                  <Books size={13} className="mt-0.5" />
                  <span>{v.sources.map((s) => s.title).slice(0, 3).join(" · ")}</span>
                </div>
              )}
            </div>
          ))}
        </div>

        {diff && (
          <div className="mt-4 border-t border-border pt-4">
            <div className="flex items-center gap-2 text-sm font-medium mb-2"><GitDiff size={16} /> Comparaison des versions</div>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <div className="text-red-600 font-mono mb-1">— Retiré</div>
                {diff.removed.length ? diff.removed.slice(0, 12).map((l, i) => <div key={i} className="bg-red-50 text-red-700 px-2 py-0.5 rounded mb-1">{l.slice(0, 90)}</div>) : <div className="text-muted-foreground">—</div>}
              </div>
              <div>
                <div className="text-emerald-700 font-mono mb-1">+ Ajouté</div>
                {diff.added.length ? diff.added.slice(0, 12).map((l, i) => <div key={i} className="bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded mb-1">{l.slice(0, 90)}</div>) : <div className="text-muted-foreground">—</div>}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
